'use client';

import { useEffect } from 'react';
import { handleDeploymentErrorReload } from '@/utils/deploymentErrorRecovery';
import { isSalesforceEmbedFrame } from '@/utils/salesforceEmbed';

/**
 * Page-level Error Boundary for Next.js App Router
 *
 * Catches errors within page segments while preserving the root layout
 * (navigation, sidebar, etc.). This is less disruptive than global-error.tsx
 * which replaces the entire document.
 *
 * For deployment-related errors (Server Action mismatch, ChunkLoadError),
 * it auto-reloads the page (deferred if a chat send is in-flight -- see
 * utils/deploymentErrorRecovery.ts). For other errors, it shows a recovery UI.
 */

const RELOAD_COUNT_KEY = 'mb_error_reload_count';
const RELOAD_COUNT_RESET_MS = 30_000; // Reset counter after 30s of stability

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Page error caught:', error);
    handleDeploymentErrorReload(error.message || '');
  }, [error]);

  // Reset the reload counter after the component has been stable for a while
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_COUNT_KEY);
      } catch {
        // Ignore
      }
    }, RELOAD_COUNT_RESET_MS);

    return () => clearTimeout(timer);
  }, []);

  const salesforceEmbed = isSalesforceEmbedFrame();

  const handleReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_COUNT_KEY);
      localStorage.removeItem('mb_build_id');
    } catch {
      // Ignore storage errors
    }
    window.location.reload();
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-8">
      <div className="max-w-md text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">
          Something went wrong
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          This might be due to a recent update. You can try again or refresh the
          page.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={reset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Try Again
          </button>
          {/* A Salesforce embed cannot sign itself back in; only reloading the Salesforce page can. */}
          {!salesforceEmbed && (
            <button
              onClick={handleReload}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          )}
        </div>
        {salesforceEmbed && (
          <p className="mt-4 text-sm text-gray-500">If this keeps happening, reload the page in Salesforce.</p>
        )}
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-6 text-left text-xs text-gray-500">
            <summary className="cursor-pointer mb-2">Error Details</summary>
            <pre className="bg-gray-900 text-gray-100 p-3 rounded-md overflow-auto max-h-48">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
              {error.digest && `\n\nDigest: ${error.digest}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
